/**
 * Le Desk — Persistance des filtres de colonnes par utilisateur / par
 * module.
 *
 * Spec rev121 (2026-02) — les filtres de colonnes des tableaux
 * (Documents, Tâches, …) survivent désormais à un rechargement de page
 * et à une reconnexion. Chaque utilisateur garde ses propres filtres
 * (clé indexée par ``user.id``) et chaque module reste indépendant
 * (Documents ≠ Tâches).
 *
 * Layout localStorage : ``ledesk_colfilters_{module}_{userId}`` → JSON
 * string.
 *
 * On garde la même « forme » de logique que la persistance déjà en
 * place pour les notifications (``ledesk_notif_tab`` /
 * ``ledesk_notif_module``) : lecture synchrone au montage, écriture
 * via useEffect à chaque changement.
 */
import { useEffect, useState } from "react";

const PREFIX = "ledesk_colfilters";

const storageKey = (module, userId) =>
  `${PREFIX}_${module}_${userId || "anon"}`;

/**
 * Hook - stateful column filters persistés dans localStorage.
 *
 * @param {string} module  Ex. "documents", "tasks".
 * @param {string} userId  ``user.id`` — permet la persistance
 *                         indépendante par utilisateur sur un même
 *                         navigateur.
 * @returns {[object, Function]}  Tuple ``[colFilters, setColFilters]``,
 *                                identique à ``useState`` (setter
 *                                accepte valeur ou callback).
 */
export function usePersistedColFilters(module, userId) {
  const [colFilters, setColFilters] = useState(() => {
    if (!userId) return {};
    try {
      const raw = localStorage.getItem(storageKey(module, userId));
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    if (!userId) return;
    try {
      // Vide → on supprime la clé pour ne pas polluer le storage.
      if (!colFilters || Object.keys(colFilters).length === 0) {
        localStorage.removeItem(storageKey(module, userId));
      } else {
        localStorage.setItem(storageKey(module, userId), JSON.stringify(colFilters));
      }
    } catch {
      /* quota / private mode : silencieux */
    }
  }, [colFilters, module, userId]);

  return [colFilters, setColFilters];
}
